import React from 'react';
import { GroupDimension, SkuGroup } from '../types';
import { fmtDate } from '../utils/dateHelpers';
import { formatQty } from '../utils/formatHelpers';
import { getSkuGroupHoldKey } from '../utils/groupHelpers';
import { StatusBadge } from '../StatusBadge/StatusBadge';
import { PoDetailPanel } from '../PoDetailPanel/PoDetailPanel';
import './SkuRow.scss';

interface SkuRowProps {
  skuGroup: SkuGroup;
  groupDimension: GroupDimension;
  isOpen: boolean;
  onToggle: () => void;
  onToggleHold: (holdKey: string) => void;
}

export const SkuRow: React.FC<SkuRowProps> = ({
  skuGroup: sg,
  groupDimension,
  isOpen,
  onToggle,
  onToggleHold,
}) => {
  const poWord = sg.pos.length > 1 ? `${sg.pos.length} POs` : '1 PO';
  const tag = `${poWord}${isOpen ? '' : ' — click for PO detail & schedule'}`;
  const holdKey = getSkuGroupHoldKey(sg.pos);

  return (
    <>
      <tr
        className={`sku-row ${sg.onHold ? 'is-onhold' : ''}`}
        onClick={onToggle}
      >
        <td>
          <div className="sku-row__name">
            {sg.onHold && <span className="sku-row__hold-flag">⏸</span>}
            {sg.sku}
          </div>
          <div className="sku-row__tag">{tag}</div>
        </td>
        <td className="sku-row__num">{formatQty(sg.ordered, sg.unit)}</td>
        <td className={`sku-row__num ${sg.pending === 0 ? 'is-zero' : ''}`}>
          {formatQty(sg.pending, sg.unit)}
        </td>
        <td
          className={`sku-row__combo ${sg.inTransitTotal === 0 ? 'is-empty' : ''}`}
        >
          {sg.inTransitTotal ? (
            <>
              <div className="sku-row__combo-primary">
                {formatQty(sg.inTransitTotal, sg.unit)}
              </div>
              <div className="sku-row__combo-secondary">
                {sg.inTransitLatestDate}
              </div>
            </>
          ) : (
            '—'
          )}
        </td>
        <td className={`sku-row__combo ${!sg.nextPlan ? 'is-empty' : ''}`}>
          {sg.nextPlan ? (
            <>
              <div className="sku-row__combo-primary">{sg.nextPlan}</div>
              <div className="sku-row__combo-secondary">
                {formatQty(sg.nextPlanQty, sg.unit)}
              </div>
            </>
          ) : (
            'not set'
          )}
        </td>
        <td>
          <StatusBadge status={sg.worstStatus} />
        </td>
      </tr>

      {isOpen && (
        <tr className="sku-row__detail">
          <td colSpan={6}>
            <div className="sku-row__detail-inner">
              <div
                className={`sku-row__hold-bar ${sg.onHold ? 'is-onhold' : ''}`}
              >
                {sg.onHold ? (
                  <>
                    <span>
                      ⏸ On hold — overdue and missing-schedule flags are
                      suppressed for this SKU.
                    </span>
                    <button type="button" onClick={() => onToggleHold(holdKey)}>
                      Resume tracking
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={() => onToggleHold(holdKey)}>
                    ⏸ Put this SKU on hold
                  </button>
                )}
              </div>

              {sg.pos.map((po) => (
                <PoDetailPanel
                  key={`${po.poNumber}-${po.url}`}
                  po={po}
                  groupDimension={groupDimension}
                />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
